import type { MapCloudPoint } from '@gpx-spa/geo-domain'
import L from 'leaflet'
import {
  ACCURACY_RING_COLOR,
  ACCURACY_RING_FILL_OPACITY,
  ACCURACY_RING_STROKE_OPACITY,
  ACCURACY_RING_STROKE_WIDTH,
  destinationEast,
  positiveAccuracyRadiusMeters,
} from '../shared/accuracy-ring-pixels.js'
import { TRACK_VERTEX_UNDER_MARKERS_PANE } from './leaflet-constants.js'

/**
 * Non-interactive canvas under the point-cloud dots.
 * Callers pass the already-thinned point set so rings stay aligned with visible dots.
 */
export class LeafletCanvasAccuracyRingLayer extends L.Layer {
  private points: MapCloudPoint[] = []
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null

  setPoints(points: readonly MapCloudPoint[]): void {
    this.points = [...points]
    this.redraw()
  }

  onAdd(map: L.Map): void {
    const pane = map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE) ?? map.getPane('overlayPane')
    this.canvas = L.DomUtil.create(
      'canvas',
      'leaflet-canvas-point-cloud-accuracy',
    ) as HTMLCanvasElement
    const canvas = this.canvas
    canvas.style.position = 'absolute'
    canvas.style.pointerEvents = 'none'
    canvas.style.zIndex = '654'
    pane?.appendChild(canvas)
    this.ctx = canvas.getContext('2d')
    map.on('moveend zoomend resize viewreset', this.redraw, this)
    this.redraw()
  }

  onRemove(map: L.Map): void {
    map.off('moveend zoomend resize viewreset', this.redraw, this)
    if (this.canvas?.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas)
    }
    this.canvas = null
    this.ctx = null
  }

  private redraw = (): void => {
    const map = this._map
    const canvas = this.canvas
    const ctx = this.ctx
    if (!map || !canvas || !ctx) return

    const size = map.getSize()
    const dpr =
      typeof window !== 'undefined' && window.devicePixelRatio
        ? window.devicePixelRatio
        : 1
    canvas.width = Math.floor(size.x * dpr)
    canvas.height = Math.floor(size.y * dpr)
    canvas.style.width = `${size.x}px`
    canvas.style.height = `${size.y}px`

    const topLeft = map.containerPointToLayerPoint([0, 0])
    L.DomUtil.setPosition(canvas, topLeft)

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, size.x, size.y)

    for (const point of this.points) {
      const meters = positiveAccuracyRadiusMeters(point.accuracyRadiusMeters)
      if (meters == null) continue
      const center = L.latLng(point.position.lat, point.position.lng)
      const edge = destinationEast(point.position.lat, point.position.lng, meters)
      const centerPx = map.latLngToContainerPoint(center)
      const edgePx = map.latLngToContainerPoint(L.latLng(edge.lat, edge.lng))
      const radius = Math.hypot(edgePx.x - centerPx.x, edgePx.y - centerPx.y)
      if (!Number.isFinite(radius) || radius <= 0) continue
      ctx.beginPath()
      ctx.arc(centerPx.x, centerPx.y, radius, 0, Math.PI * 2)
      ctx.fillStyle = ACCURACY_RING_COLOR
      ctx.globalAlpha = ACCURACY_RING_FILL_OPACITY
      ctx.fill()
      ctx.globalAlpha = ACCURACY_RING_STROKE_OPACITY
      ctx.strokeStyle = ACCURACY_RING_COLOR
      ctx.lineWidth = ACCURACY_RING_STROKE_WIDTH
      ctx.stroke()
      ctx.globalAlpha = 1
    }
  }
}

export function createLeafletCanvasAccuracyRingLayer(): LeafletCanvasAccuracyRingLayer {
  return new LeafletCanvasAccuracyRingLayer()
}
